import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { AccountLinksContext } from './AccountLinksContext'
import accountLinksApi from '../../../Libs/installation-rights-management/api/account-link.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { AccountLinkModel } from '../models'

interface Props {
  children?: React.ReactNode
}
/**
 * This provider gives the information on if a user has only read or also write rights for a given installation.
 *
 * This is especially relevant for users who are invited to an installation (user, installer, or partner).
 * Those users are linked to the installation via the accountLinks, which includes the information about
 * their access rights.
 *
 * For higher user levels, such as service, this linkage is irrelevant. Instead, those levels can generally access all installations with writing rights.
 *
 */
const AccountLinksProvider: React.FC<Props> = ({ children }) => {
  const [accountLinks, setAccountLinks] = useState<AccountLinkModel[]>([])
  const [isLoadingAccountLinks, setIsLoadingAccountLinks] = useState(true)
  const authFetch = useAuthFetchWithResponse()
  const activeInstallation = useTypedSelector(state => state.storage.activeInstallation)
  const [refreshToggle, setRefreshToggle] = useState(false)

  useEffect(() => {
    getAccountLinks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInstallation, refreshToggle])

  const updateAccountLinks = useCallback((): void => {
    setRefreshToggle(!refreshToggle)
  }, [refreshToggle])

  const getAccountLinks = useCallback(() => {
    if (activeInstallation) {
      setIsLoadingAccountLinks(true)
      accountLinksApi
        .findAll(activeInstallation, authFetch)
        .then(res => {
          if (res.hasErrors) return console.error(res.error)
          if (res.value) {
            setAccountLinks(res.value)
          }
        })
        .catch(err => console.error(err))
        .finally(() => setIsLoadingAccountLinks(false))
    }
  }, [activeInstallation, authFetch])

  const value = useMemo(
    () => ({ accountLinks, isLoadingAccountLinks, updateAccountLinks }),
    [accountLinks, isLoadingAccountLinks, updateAccountLinks]
  )

  return <AccountLinksContext.Provider value={value}>{children}</AccountLinksContext.Provider>
}

export default AccountLinksProvider
